import { gql } from '@apollo/client';
import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import ExitIcon from '@moda/icons/exit-24';
import { Clickable, Modal } from '@moda/om';
import { compact } from '@moda/portal-stanchions';
import {
  MobileLookviewPopupSizeSelectorFragment as Variant,
  MobileLookviewPopupSizeSelectorInventoryFragment as Inventory
} from '../../../../generated/types';
import { AddToBagButton, ADD_TO_BAG_FRAGMENT } from '../../../../components/AddToBagButton';

import './MobileLookviewPopupSizeSelector.scss';

export const MOBILE_LOOKVIEW_POPUP_SIZE_SELECTOR_INVENTORY_FRAGMENT = gql`
  fragment MobileLookviewPopupSizeSelectorInventoryFragment on Inventory {
    size
    available
    canSell: can_sell
  }
`;

export const MOBILE_LOOKVIEW_POPUP_SIZE_SELECTOR_VARIANT_FRAGMENT = gql`
  fragment MobileLookviewPopupSizeSelectorVariantFragment on VariantData {
    inventory {
      ...MobileLookviewPopupSizeSelectorInventoryFragment
    }
  }
  ${MOBILE_LOOKVIEW_POPUP_SIZE_SELECTOR_INVENTORY_FRAGMENT}
`;

export const MOBILE_LOOKVIEW_POPUP_SIZE_SELECTOR_FRAGMENT = gql`
  fragment MobileLookviewPopupSizeSelectorFragment on Variant {
    masterVariant: master_variants_data {
      id
      ...MobileLookviewPopupSizeSelectorVariantFragment
    }
    ...AddToBagFragment
  }
  ${MOBILE_LOOKVIEW_POPUP_SIZE_SELECTOR_VARIANT_FRAGMENT}
  ${ADD_TO_BAG_FRAGMENT}
`;

interface Props {
  variant: Variant;
  show: boolean;
  onClose: () => void;
}

export const MobileLookviewPopupSizeSelector: React.FC<Props> = ({ variant, show, onClose }) => {
  const [selectedInventory, selectInventory] = useState<Inventory | null>(null);

  const handlePropagation = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => event.stopPropagation(),
    []
  );

  const { masterVariant: activeVariant } = variant;
  const availableInventory = compact(activeVariant.inventory);

  if (!show) {
    return null;
  }

  return (
    <Modal className="MobileLookviewPopupSizeSelector" onClose={onClose}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className="MobileLookviewPopupSizeSelector__content"
        onClick={handlePropagation}
        role="dialog"
      >
        <Clickable className="MobileLookviewPopupSizeSelector__close" onClick={onClose}>
          <ExitIcon />
        </Clickable>

        <div className="MobileLookviewPopupSizeSelector__list">
          <header className="MobileLookviewPopupSizeSelector__header">Select Your Size:</header>

          {availableInventory.map((inventory, index) => (
            <Clickable
              key={inventory.size || index}
              className={classNames('MobileLookviewPopupSizeSelector__item', {
                'MobileLookviewPopupSizeSelector__item--sold-out': !inventory.canSell,
                'MobileLookviewPopupSizeSelector__item--selected':
                  inventory.size === selectedInventory?.size
              })}
              disabled={!inventory.canSell}
              onClick={() => {
                if (inventory.canSell) selectInventory(inventory);
              }}
            >
              {inventory.size}
            </Clickable>
          ))}
        </div>

        <div className="MobileLookviewPopupSizeSelector__actions">
          <AddToBagButton
            className="MobileLookviewPopupSizeSelector__button"
            size={selectedInventory}
            activeVariantId={activeVariant.id}
            validatesSizePresenceOn="always"
            variant={variant}
            onAdded={onClose}
          />
        </div>
      </div>
    </Modal>
  );
};
